import React, { useEffect, useState } from "react";
import useReveal from "@/hooks/useReveal";
import { U } from "@/data/services";
import { insertRow, uploadFile, notifyForm } from "@/lib/forms";
import { listJobs, JobListingRow } from "@/lib/api";
import { useSettings } from "@/lib/SettingsContext";

const HERO_BG = U("1465847899084-d164df4dedc6", 2400, 88);

const DEFAULT_NO_JOBS = "We don't have any open positions right now, but we're always looking for great talent. Send your CV to careers@ideationav.com";

interface ApplyState {
  job: JobListingRow | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  experience: string;
  portfolio: string;
  coverNote: string;
  cv: File | null;
}

const BLANK: ApplyState = {
  job: null, firstName: "", lastName: "", email: "", phone: "",
  experience: "", portfolio: "", coverNote: "", cv: null,
};

const stripHtml = (s: string | undefined | null): string => {
  if (!s) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = s;
  return (tmp.textContent || tmp.innerText || "").trim();
};

// Defensive dedupe: if a string is exactly the same value concatenated with itself
// (e.g. "DubaiDubai"), collapse to a single copy.
const dedupe = (s: string | undefined | null): string => {
  const v = (s || "").trim();
  if (!v) return "";
  const half = v.length / 2;
  if (Number.isInteger(half) && v.slice(0, half).toLowerCase() === v.slice(half).toLowerCase()) {
    return v.slice(0, half);
  }
  return v;
};

const Hiring: React.FC = () => {
  useReveal();
  const { settings } = useSettings();
  const [jobs, setJobs] = useState<JobListingRow[] | null>(null); // null = loading
  const [apply, setApply] = useState<ApplyState>(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    listJobs(true).then(setJobs).catch(() => setJobs([]));
  }, []);

  const open = (job: JobListingRow) => { setApply({ ...BLANK, job }); setResult(null); };
  const close = () => { setApply(BLANK); setResult(null); };

  useEffect(() => {
    if (!apply.job) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [apply.job]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apply.job) return;
    setSubmitting(true); setResult(null);
    try {
      let cv_url: string | null = null;
      if (apply.cv) {
        const u = await uploadFile("uploads", "cv", apply.cv);
        cv_url = u.url;
      }
      await insertRow("job_applications", {
        job_id: apply.job.id || null,
        job_title: apply.job.title,
        first_name: apply.firstName,
        last_name: apply.lastName,
        email: apply.email,
        phone: apply.phone,
        experience: apply.experience,
        portfolio_url: apply.portfolio,
        cover_note: apply.coverNote,
        cv_url,
      });
      notifyForm({
        kind: "job",
        to: settings.notification_email,
        jobTitle: apply.job.title,
        data: { first_name: apply.firstName, last_name: apply.lastName, email: apply.email, phone: apply.phone, experience: apply.experience, portfolio_url: apply.portfolio, cover_note: apply.coverNote, cv_url, job_title: apply.job.title },
      }).catch(() => {});
      setResult({ ok: true, msg: "Thank you — your application has been received. We'll be in touch within 24 hours." });
      setTimeout(close, 2200);
    } catch (err: any) {
      setResult({ ok: false, msg: err?.message || "Something went wrong. Please email info@ideationav.com." });
    } finally {
      setSubmitting(false);
    }
  };

  const noJobsMessage = (settings.no_jobs_message && settings.no_jobs_message.trim()) || DEFAULT_NO_JOBS;
  const isLoading = jobs === null;
  const hasJobs = !!jobs && jobs.length > 0;

  return (
    <main className="iav-page" data-testid="hiring-page">
      <section className="iav-page-hero iav-hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="iav-page-hero-overlay" />
        <div className="iav-page-hero-inner">
          <span className="iav-tag-line" data-reveal>Join Our Team</span>
          <h1 data-reveal>WE'RE<br /><span className="iav-accent">HIRING.</span></h1>
          <p className="lead" data-reveal>We are growing — and looking for talented professionals to join the Ideation team full time. If you are passionate about live events and technical production, we want to hear from you.</p>
        </div>
      </section>

      {!isLoading && !hasJobs && (
        <div
          className="iav-no-jobs"
          data-testid="no-jobs-message"
          style={{
            maxWidth: 600,
            margin: "80px auto",
            padding: "0 24px",
            textAlign: "center",
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 500,
            fontSize: 15,
            lineHeight: 1.75,
            color: "rgba(255,255,255,0.55)",
          }}
        >
          {noJobsMessage}
        </div>
      )}

      {hasJobs && (
        <div className="iav-jobs" data-testid="job-listings">
          {jobs!.map(job => {
            const id = job.id || job.slug || job.title;
            const tags = Array.isArray(job.tags) ? job.tags : [];
            return (
              <article key={id} className="iav-job-card" data-reveal data-testid={`job-${id}`}>
                <div className="iav-job-meta">
                  <span className="iav-job-pill">{dedupe(job.employment_type) || "Full Time"}</span>
                  <span className="iav-job-pill">{dedupe(job.location) || "Dubai"}</span>
                  <span className="iav-job-pill">India</span>
                </div>
                <h3 className="iav-job-title">{job.title}</h3>
                <p className="iav-job-desc">{stripHtml(job.description)}</p>
                {tags.length > 0 && (
                  <div className="iav-job-tags">
                    {tags.map((t: string) => <span key={t} className="iav-job-tag">{t}</span>)}
                  </div>
                )}
                <button type="button" onClick={() => open(job)} className="iav-btn iav-btn-primary iav-job-cta" data-cursor="cta" data-testid={`apply-${id}`}>Apply Now</button>
              </article>
            );
          })}
        </div>
      )}

      {apply.job && (
        <div className="iav-modal" role="dialog" aria-modal="true" aria-labelledby="apply-title" onClick={close} data-testid="apply-modal">
          <div className="iav-modal-panel" onClick={e => e.stopPropagation()}>
            <button className="iav-modal-close" onClick={close} aria-label="Close" data-testid="apply-close">✕</button>
            <div className="iav-modal-sub">Apply for</div>
            <h2 id="apply-title" className="iav-modal-title">{apply.job.title}</h2>
            <form className="iav-form" onSubmit={onSubmit}>
              <div className="iav-form-row">
                <div className="iav-field"><label>First Name *</label><input required className="iav-input" value={apply.firstName} onChange={e => setApply({ ...apply, firstName: e.target.value })} /></div>
                <div className="iav-field"><label>Last Name *</label><input required className="iav-input" value={apply.lastName} onChange={e => setApply({ ...apply, lastName: e.target.value })} /></div>
              </div>
              <div className="iav-form-row">
                <div className="iav-field"><label>Email *</label><input required type="email" className="iav-input" value={apply.email} onChange={e => setApply({ ...apply, email: e.target.value })} /></div>
                <div className="iav-field"><label>Phone *</label><input required type="tel" className="iav-input" value={apply.phone} onChange={e => setApply({ ...apply, phone: e.target.value })} /></div>
              </div>
              <div className="iav-field"><label>Position</label><input className="iav-input" value={apply.job.title} readOnly /></div>
              <div className="iav-field"><label>Years of Experience</label>
                <select className="iav-select" value={apply.experience} onChange={e => setApply({ ...apply, experience: e.target.value })}>
                  <option value="">Select…</option>
                  <option>Less than 1 year</option>
                  <option>1 – 3 years</option>
                  <option>3 – 5 years</option>
                  <option>5 – 8 years</option>
                  <option>8+ years</option>
                </select>
              </div>
              <div className="iav-field"><label>Portfolio / LinkedIn URL</label><input className="iav-input" placeholder="https://" value={apply.portfolio} onChange={e => setApply({ ...apply, portfolio: e.target.value })} /></div>
              <div className="iav-field"><label>Cover Note</label><textarea className="iav-textarea" value={apply.coverNote} onChange={e => setApply({ ...apply, coverNote: e.target.value })} placeholder="Tell us about your relevant experience…" /></div>
              <div className="iav-field"><label>Upload CV (PDF preferred, max 10MB)</label>
                <label className="iav-file" data-testid="apply-cv-input">
                  <input type="file" accept=".pdf,.doc,.docx" onChange={e => setApply({ ...apply, cv: e.target.files?.[0] || null })} />
                  <div>{apply.cv ? apply.cv.name : "Drag & drop or click to upload"}</div>
                  <small>PDF · DOC · DOCX</small>
                </label>
              </div>
              {result && <div className={`iav-form-msg ${result.ok ? "is-ok" : "is-err"}`} data-testid="apply-result">{result.msg}</div>}
              <button type="submit" disabled={submitting} className="iav-btn iav-btn-primary" data-testid="apply-submit">{submitting ? "Submitting…" : "Submit Application"}</button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};
export default Hiring;
